import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ALL_OPTIONS, waLink } from "../../lib/catalog";
import { api, errMsg } from "../../lib/api";
import { EASE } from "./Motion";

const minDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 3);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const empty = { name: "", phone: "", email: "", event_date: "", category: "", location: "", message: "" };
const field = "h-12 rounded-xl border-[#D4AF37]/25 bg-[#190317]/60 text-[#FFF8EF] placeholder:text-[#E8D8C8]/60 focus-visible:ring-[#D4AF37]";

const Field = ({ id, label, children }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="font-cinzel text-[11px] tracking-[0.2em] text-[#F3E5AB]">{label}</Label>
    {children}
  </div>
);

const Success = ({ sent, onReset }) => (
  <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, ease: EASE }} className="flex flex-col items-start gap-5 py-6" data-testid="enquiry-success">
    <CheckCircle2 size={44} className="text-[#25D366]" />
    <h3 className="font-display text-4xl text-[#FFF8EF]">Thank you, {sent.name.split(" ")[0]}.</h3>
    <p className="text-[#E8D8C8]">Your enquiry for <span className="text-[#F3E5AB]">{sent.category_name}</span> on {sent.event_date} is with us. We'll call you back shortly.</p>
    <div className="flex flex-wrap gap-3">
      <a href={waLink(`Hi SkandaSri Eventz, I just sent an enquiry: ${sent.category_name} on ${sent.event_date}. Name: ${sent.name}.`)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-3 text-sm font-semibold text-[#06260f]" data-testid="enquiry-success-whatsapp">
        <MessageCircle size={16} /> Also send on WhatsApp
      </a>
      <button onClick={onReset} className="rounded-full border border-[#D4AF37]/40 px-5 py-3 text-sm text-[#F3E5AB]" data-testid="enquiry-new-btn">Send another</button>
    </div>
  </motion.div>
);

export const EnquiryForm = ({ preset }) => {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const min = useMemo(minDate, []);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  useEffect(() => {
    if (!preset?.category) return;
    setForm((f) => ({ ...f, category: preset.category, message: preset.photo ? `I liked "${preset.photo}". ` : f.message }));
  }, [preset]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category) return toast.error("Please choose what you'd like to book");
    if (form.event_date < min) return toast.error("Please pick a date at least three days from today");
    setBusy(true);
    try {
      const { data } = await api.post("/enquiries", { ...form, email: form.email.trim() || null, photo_ref: preset?.photo || null });
      setSent(data);
      setForm(empty);
      toast.success("Enquiry received — we'll be in touch soon");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent) return <Success sent={sent} onReset={() => setSent(null)} />;

  return (
    <form onSubmit={submit} className="grid gap-5 sm:grid-cols-2" data-testid="enquiry-form">
      <Field id="enq-name" label="Full name">
        <Input id="enq-name" required minLength={2} value={form.name} onChange={set("name")} placeholder="Your name" className={field} data-testid="enquiry-name-input" />
      </Field>
      <Field id="enq-phone" label="Phone / WhatsApp">
        <Input id="enq-phone" required type="tel" minLength={7} value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={field} data-testid="enquiry-phone-input" />
      </Field>
      <Field id="enq-date" label="Event / delivery date">
        <Input id="enq-date" required type="date" min={min} value={form.event_date} onChange={set("event_date")} className={`${field} [color-scheme:dark]`} data-testid="enquiry-date-input" />
      </Field>
      <Field id="enq-cat" label="What would you like?">
        <Select value={form.category} onValueChange={(v) => v && set("category")(v)}>
          <SelectTrigger id="enq-cat" className={field} data-testid="enquiry-category-select"><SelectValue placeholder="Choose a category" /></SelectTrigger>
          <SelectContent data-lenis-prevent className="max-h-72 border-[#D4AF37]/30 bg-[#1d051a] text-[#FFF8EF]">
            {ALL_OPTIONS.map((o) => <SelectItem key={o.slug} value={o.slug} data-testid={`enquiry-option-${o.slug}`}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <div className="sm:col-span-2">
        <Field id="enq-email" label="Email (optional, for your copy)">
          <Input id="enq-email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com — we'll email you a copy of your enquiry" className={field} data-testid="enquiry-email-input" />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <Field id="enq-loc" label="Delivery location / venue">
          <Input id="enq-loc" value={form.location} onChange={set("location")} placeholder="Area, venue or address" className={field} data-testid="enquiry-location-input" />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <Field id="enq-msg" label="Your requirements">
          <Textarea id="enq-msg" rows={4} value={form.message} onChange={set("message")} placeholder="Colours, flowers, size, theme…" className="rounded-xl border-[#D4AF37]/25 bg-[#190317]/60 text-[#FFF8EF] placeholder:text-[#E8D8C8]/60 focus-visible:ring-[#D4AF37]" data-testid="enquiry-message-input" />
        </Field>
      </div>
      <div className="flex flex-col gap-3 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-[#E8D8C8]">Earliest available date: {min.split("-").reverse().join("/")} (three days' notice).</p>
        <button type="submit" disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-full bg-gold-foil px-8 py-4 text-sm font-semibold text-[#240620] transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60" data-testid="enquiry-submit-btn">
          {busy && <Loader2 size={16} className="animate-spin" />} Send enquiry
        </button>
      </div>
    </form>
  );
};
